import React, { useEffect, useRef, useState } from 'react';
import { Phone } from 'lucide-react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

import chatStyles from './ChatInterface.module.css';
import roomBookingIcon from '../../../assets/svg/ai-sales-landing/room_booking.svg';
import modifyBookingIcon from '../../../assets/svg/ai-sales-landing/booking_modify.svg';
import cancelBookingIcon from '../../../assets/svg/ai-sales-landing/cancelbooking_usecase.svg';
import refundBookingIcon from '../../../assets/svg/ai-sales-landing/refund_usecase.svg';
import { fireGtmEvent } from '../../../utils/googleAnalytics';
import { getCallBackForAI } from '../../../Services/mainPageService';

const ChatbotDemo = () => {
  const scriptTagId = '5ee7fc4f-43e0-48e5-9b4f-d6b374359fd9';
  const chainId = '1';
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!iframeRef.current) return;

    const iframeDoc =
      iframeRef.current.contentDocument ||
      iframeRef.current.contentWindow.document;

    iframeDoc.open();
    iframeDoc.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <style>
          html, body {
            margin: 0;
            padding: 0;
            width: 100%;
            height: 100%;
            overflow: hidden;
          }
          #hotelzify-chatbot-root {
            width: 100%;
            height: 100%;
          }
        </style>
      </head>
      <body>
        <div id="hotelzify-chatbot-root">
          <div 
            id="asc-chatbot"
            data-script-tag-id="${scriptTagId}"
            hotelzify_chain_id="${chainId}"
            auto-open="true"
            auto-open-delay="1000"
          ></div>
        </div>
        <script type="module" src="https://chatwidget.hotelzify.com/bundle.js"></script>
      </body>
      </html>
    `);
    iframeDoc.close();
  }, [iframeRef]);

  return (
    <div className={chatStyles.chatbotContainer}>
      <iframe
        ref={iframeRef}
        className={chatStyles.chatbotIframe}
        title="Chat Agent"
        sandbox="allow-scripts allow-same-origin allow-forms"
      />
    </div>
  );
};

const ChatInterface = () => {
  const [activeTab, setActiveTab] = useState('voice');
  const [selectedUsecase, setSelectedUsecase] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneInputFocus, setPhoneInputFocus] = useState(false);
  const [voiceCallInitiated, setVoiceCallInitiated] = useState(false);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleUsecaseClick = (usecase) => {
    setSelectedUsecase(usecase);
  };

  const handlePhoneChange = (value) => {
    setPhoneNumber(value);
    setVoiceCallInitiated(false);
  };

  const handlePhoneFocus = () => {
    setPhoneInputFocus(true);
  };

  const handlePhoneBlur = () => {
    setPhoneInputFocus(false);
  };

  const handleAutomattedCall = async () => {
   const obj = {
      phone: phoneNumber,
    };
    await getCallBackForAI(obj);
    setVoiceCallInitiated(true);
    fireGtmEvent('ai-sales-automatted-call', { phoneNumber });
    setPhoneNumber('');
  };

  return (
    <div className={chatStyles.deviceWrapper}>
      <div className={chatStyles.iphoneX}>
        <div className={chatStyles.notch}>
          <div className={chatStyles.speaker}></div>
          <div className={chatStyles.camera}></div>
        </div>

        <div className={chatStyles.leftButton}></div>
        <div className={chatStyles.leftButton2}></div>
        <div className={chatStyles.leftButton3}></div>
        <div className={chatStyles.rightButton}></div>

        <div className={chatStyles.screen}>
          <div className={chatStyles.container}>
            { activeTab === "voice" &&
              <>
                <div className={chatStyles.topWave}></div>
                <div className={chatStyles.header}>
                  <div className={chatStyles.headerContent}>
                    <span className={chatStyles.waveHand}>👋</span>
                    <div>
                      <div className={chatStyles.experienceText}>
                        Experience Our AI
                      </div>
                      <div className={chatStyles.experienceText}>
                        Agent Now!
                      </div>
                    </div>
                  </div>
                </div>
              </>
            }
            <div className={chatStyles.buttonGroup}>
              <button
                className={`${chatStyles.button} ${
                  activeTab === "voice" ? chatStyles.activeButton : ""
                }`}
                onClick={() => handleTabClick("voice")}
              >
                Voice Agent
              </button>
              <button
                className={`${chatStyles.button} ${
                  activeTab === "chat" ? chatStyles.activeButton : ""
                }`}
                onClick={() => handleTabClick("chat")}
              >
                Chat Agent
              </button>
            </div>
            {activeTab === "voice" ? (
              <>
                <div className={chatStyles.phoneInput}>
                  <PhoneInput
                    defaultCountry="IN"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    onFocus={handlePhoneFocus}
                    onBlur={handlePhoneBlur}
                    className={`${chatStyles.phoneInputWrapper} ${
                      phoneInputFocus ? "PhoneInput--focus" : ""
                    }`}
                    placeholder="Mobile Number"
                    required
                  />
                </div>

                <button
                  className={chatStyles.callButton}
                  disabled={!phoneNumber}
                  onClick={handleAutomattedCall}
                >
                  <Phone size={18} />
                  <span>Call Me Now</span>
                </button>
                {voiceCallInitiated && (
                  <div className={chatStyles.callText}>
                    Our Agent will call you within seconds.
                  </div>
                )}
                <div className={chatStyles.instructionText}>
                  Select a usecase to get started
                </div>

                <div className={chatStyles.usecaseGrid}>
                  <div
                    className={`${chatStyles.usecaseCard} ${
                      selectedUsecase === "room" ? chatStyles.selectedCard : ""
                    }`}
                    onClick={() => handleUsecaseClick("room")}
                  >
                    <span className={chatStyles.roomIcon}>
                      <img src={roomBookingIcon} alt="RoomBooking" />
                    </span>
                    <span>
                      Room
                      <br />
                      Booking
                    </span>
                  </div>
                  <div
                    className={`${chatStyles.usecaseCard} ${
                      selectedUsecase === "modification"
                        ? chatStyles.selectedCard
                        : ""
                    }`}
                    onClick={() => handleUsecaseClick("modification")}
                  >
                    <span className={chatStyles.modIcon}>
                      <img src={modifyBookingIcon} alt="bookingModification" />
                    </span>
                    <span>
                      Booking
                      <br />
                      Modification
                    </span>
                  </div>
                  <div
                    className={`${chatStyles.usecaseCard} ${
                      selectedUsecase === "cancel"
                        ? chatStyles.selectedCard
                        : ""
                    }`}
                    onClick={() => handleUsecaseClick("cancel")}
                  >
                    <span className={chatStyles.cancelIcon}>
                      <img src={cancelBookingIcon} alt="cancelBooking" />
                    </span>
                    <span>
                      Cancel
                      <br />
                      Booking
                    </span>
                  </div>
                  <div
                    className={`${chatStyles.usecaseCard} ${
                      selectedUsecase === "refund"
                        ? chatStyles.selectedCard
                        : ""
                    }`}
                    onClick={() => handleUsecaseClick("refund")}
                  >
                    <span className={chatStyles.refundIcon}>
                      <img src={refundBookingIcon} alt="refundBooking" />
                    </span>
                    <span>
                      Refund
                      <br />
                      Status
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <ChatbotDemo />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
